/**
 * 세션 내 API 호출 기록 — ApiInfoPanel에서 실제 요청/응답 표시용
 * Authorization 등 민감 필드는 sanitizeForDisplay에서 마스킹
 */

/** 패널·디버그용 오류 구분 (기술적 추정) */
export type DebugErrorCategory = 'backend' | 'frontend' | 'design' | 'unknown';

export type ApiCallRecord = {
	id: string;
	at: number;
	source: 'main-api' | 'platform-api';
	method: string;
	/** apiClient에 넘긴 path (쿼리 제외) */
	path: string;
	fullUrl?: string;
	queryParams?: Record<string, unknown> | null;
	requestBody?: unknown;
	status?: number;
	responseBody?: unknown;
	errorMessage?: string;
	/** 실패 시 기록 — HTTP/네트워크는 주로 backend */
	errorCategory?: DebugErrorCategory;
};

export type SessionDebugErrorEntry = {
	id: string;
	at: number;
	source: 'react-query' | 'react-query-mutation' | 'manual';
	category: DebugErrorCategory;
	title: string;
	message: string;
	/** React Query queryKey 등 */
	queryKey?: string;
	stack?: string;
};

const MAX_RECORDS = 100;
const MAX_SESSION_ERRORS = 60;
let records: ApiCallRecord[] = [];
let sessionDebugErrors: SessionDebugErrorEntry[] = [];
const listeners = new Set<() => void>();

function notify(): void {
	listeners.forEach((fn) => fn());
}

/** HTTP 상태·메시지로 API 레코드 분류 (레거시 레코드 보강에도 사용) */
export function classifyHttpLikeError(status?: number, message?: string): DebugErrorCategory {
	if (status != null && status >= 400 && status < 600) return 'backend';
	if (status === 0) return 'backend';
	if (message) {
		if (/SyntaxError|JSON|parse|Unexpected token|is not valid JSON/i.test(message)) return 'frontend';
		if (/Network|Failed to fetch|Load failed|ECONNREFUSED|ENOTFOUND|fetch/i.test(message)) return 'backend';
		if (/timeout|Timeout|AbortError|408/i.test(message)) return 'backend';
		if (/^\d{3}\s/.test(message) || /API Error|Platform API Error/i.test(message)) return 'backend';
	}
	if (status != null && status >= 200 && status < 300) return 'unknown';
	return 'unknown';
}

export function deriveCategoryFromRecord(rec: ApiCallRecord): DebugErrorCategory {
	if (rec.errorCategory) return rec.errorCategory;
	const failed = Boolean(rec.errorMessage) || (rec.status != null && rec.status >= 400);
	if (!failed) return 'unknown';
	return classifyHttpLikeError(rec.status, rec.errorMessage);
}

export function classifyUnknownError(error: unknown): DebugErrorCategory {
	if (error && typeof error === 'object' && 'status' in error) {
		const s = (error as { status?: number }).status;
		if (typeof s === 'number' && s >= 400 && s < 600) return 'backend';
	}
	if (error instanceof TypeError || error instanceof ReferenceError) return 'frontend';
	if (error instanceof SyntaxError) return 'frontend';
	if (error instanceof Error) {
		return classifyHttpLikeError(undefined, error.message);
	}
	return 'unknown';
}

const CATEGORY_LABEL: Record<DebugErrorCategory, string> = {
	backend: '백엔드·API',
	frontend: '프론트엔드(클라이언트)',
	design: '디자인·기획·데이터 기대',
	unknown: '미분류',
};

export function getDebugCategoryLabel(category: DebugErrorCategory): string {
	return CATEGORY_LABEL[category] ?? category;
}

export function getExplanationForCategory(category: DebugErrorCategory): string {
	switch (category) {
		case 'backend':
			return '서버가 HTTP 오류(4xx/5xx)를 반환했거나, 네트워크·타임아웃·연결 실패로 요청이 끝까지 가지 못한 경우입니다. apiClient/platformApi 기록과 대조하세요.';
		case 'frontend':
			return '브라우저에서 스크립트 예외가 났거나, 응답 파싱·타입 가정이 맞지 않는 경우입니다. React Query 오류 중 일부도 여기에 해당할 수 있습니다.';
		case 'design':
			return 'API는 성공(2xx)인데 화면·기획 조건과 맞지 않는 경우(빈 목록 등) 수동으로 표시한 항목입니다.';
		default:
			return '분류 정보가 없거나 복합적인 경우입니다. 아래 기술 상세를 확인하세요.';
	}
}

export function recordSessionDebugError(
	entry: Omit<SessionDebugErrorEntry, 'id' | 'at'>
): void {
	const id = `se-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
	sessionDebugErrors = [...sessionDebugErrors, { ...entry, id, at: Date.now() }].slice(-MAX_SESSION_ERRORS);
	notify();
}

export function getSessionDebugErrors(): SessionDebugErrorEntry[] {
	return sessionDebugErrors;
}

export function subscribeApiDebug(onStoreChange: () => void): () => void {
	listeners.add(onStoreChange);
	return () => listeners.delete(onStoreChange);
}

export function getApiDebugRecords(): ApiCallRecord[] {
	return records;
}

export function recordApiDebug(entry: Omit<ApiCallRecord, 'id' | 'at'>): void {
	const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
	records = [...records, { ...entry, id, at: Date.now() }].slice(-MAX_RECORDS);
	notify();
}

/** 패널의 `/organization/{organizationId}/detail` 같은 패턴과 실제 `/organization/3/detail` 매칭 */
export function pathMatchesPattern(pattern: string, actualPath: string): boolean {
	if (pattern === actualPath) return true;
	const withRegex = pattern.replace(/\{[^}]+\}/g, '___PARAM___');
	const escaped = withRegex.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/___PARAM___/g, '[^/]+');
	try {
		return new RegExp(`^${escaped}$`).test(actualPath);
	} catch {
		return false;
	}
}

/**
 * React Query 세션 오류 직전에 apiClient에 기록된 실패 HTTP 호출을 추정해 연결
 * (시간 근접 + 선택적 메시지 유사도)
 */
export function findRelatedApiRecordForSessionError(
	entry: SessionDebugErrorEntry,
	list: ApiCallRecord[]
): ApiCallRecord | undefined {
	const t0 = entry.at;
	let best: ApiCallRecord | undefined;
	let bestScore = -1;
	const msgPrefix = (entry.message || '').slice(0, 80);
	for (let i = list.length - 1; i >= 0; i--) {
		const r = list[i];
		const failed = Boolean(r.errorMessage) || (r.status != null && r.status >= 400);
		if (!failed) continue;
		const dt = t0 - r.at;
		if (dt < 0 || dt > 25_000) continue;
		let score = 1_000_000 - dt;
		if (msgPrefix && r.errorMessage && (msgPrefix.includes(r.errorMessage.slice(0, 40)) || r.errorMessage.includes(msgPrefix.slice(0, 40)))) {
			score += 8000;
		}
		if (score > bestScore) {
			bestScore = score;
			best = r;
		}
	}
	return best;
}

export function findLatestMatchingRecord(
	endpointPath: string,
	method: string,
	list: ApiCallRecord[]
): ApiCallRecord | undefined {
	const m = method.toUpperCase();
	for (let i = list.length - 1; i >= 0; i--) {
		const r = list[i];
		if (r.method.toUpperCase() !== m) continue;
		if (pathMatchesPattern(endpointPath, r.path)) return r;
	}
	return undefined;
}

export function sanitizeForDisplay(value: unknown, depth = 0): unknown {
	if (depth > 12) return '[max depth]';
	if (value === null || value === undefined) return value;
	if (Array.isArray(value)) return value.map((v) => sanitizeForDisplay(v, depth + 1));
	if (typeof value === 'object') {
		const o = value as Record<string, unknown>;
		const out: Record<string, unknown> = {};
		for (const [k, v] of Object.entries(o)) {
			if (/^(accessToken|refreshToken|password|pw|secret|authorization)$/i.test(k)) {
				out[k] = '[redacted]';
			} else {
				out[k] = sanitizeForDisplay(v, depth + 1);
			}
		}
		return out;
	}
	return value;
}

export function stringifyForPanel(value: unknown, maxLen = 12000): string {
	try {
		const sanitized = sanitizeForDisplay(value);
		const s = JSON.stringify(sanitized, null, 2);
		if (s.length > maxLen) return `${s.slice(0, maxLen)}\n… (truncated)`;
		return s;
	} catch {
		return String(value);
	}
}

export function summarizeRequestBody(body: unknown): unknown {
	if (body instanceof FormData) {
		const keys: string[] = [];
		body.forEach((_v, k) => {
			if (!keys.includes(k)) keys.push(k);
		});
		return { __type: 'FormData', keys };
	}
	return body;
}
